import Page from "../components/Page/Page";
import {
  VirtualScroller,
  WebGLIsland,
  RenderTileArgs,
  FPSMeter,
} from "@yv/sonic-ui-library";
import { Container } from "pixi.js";
import { extend } from "@pixi/react";
import { CarouselDataWrapper } from "../components/CarouselDataWrapper";
import { getAllCarouselTypes } from "../hooks/tmdb/carousels";
import { TILE_GAP, tileHeight, VERTICAL_PADDING } from "./config";
import { useCallback } from "react";
import { useSearch } from "@tanstack/react-router";

extend({ Container });

const viewportWidth = 1280;
const viewportHeight = 720;

const carousels = getAllCarouselTypes();
const PADDING = 80;
const TITLE_HEIGHT = 70; // Height reserved for carousel title and spacing
const carouselItemHeight = tileHeight + TITLE_HEIGHT + TILE_GAP;

export function WebGlUIPage() {
  const { resolution, autoDensity, antialias, renderPreference, fpsMeter } =
    useSearch({
      from: "/",
    });

  // Function to calculate the height of each carousel row based on its tile size
  const getCarouselSize = useCallback((index: number) => {
    const carousel = carousels[index];
    const tileSize = carousel.tileSize;
    return {
      width: viewportWidth,
      height: tileSize.height + TITLE_HEIGHT + TILE_GAP,
    };
  }, []);

  const renderCarousel = useCallback(function renderCarousel({
    index: carouselIndex,
    isFocused,
    x,
    y,
  }: RenderTileArgs) {
    const Item = carousels[carouselIndex];
    const urlTemplate = Item.apiTemplate;
    const tileSize = Item.tileSize;
    const carouselTitle = Item.name;
    const carouselRowHeight = tileSize.height + TITLE_HEIGHT + TILE_GAP;
    return (
      <CarouselDataWrapper
        key={`carousel-row-${carouselIndex}`}
        carouselTitle={carouselTitle}
        urlTemplate={urlTemplate}
        carouselIndex={carouselIndex}
        isCarouselFocused={isFocused}
        viewportWidth={viewportWidth}
        carouselItemHeight={carouselRowHeight}
        tileWidth={tileSize.width}
        tileHeight={tileSize.height}
        position={{ x, y }}
        padding={PADDING}
        gap={TILE_GAP}
      />
    );
  },
  []);
  return (
    <Page>
      <WebGLIsland
        width={viewportWidth}
        height={viewportHeight}
        resolution={resolution}
        roundPixels={true}
        antialias={antialias}
        autoDensity={autoDensity}
        preference={renderPreference}
        useBackBuffer={false}
        backgroundAlpha={0}
      >
        <VirtualScroller
          listIndex={0}
          virtualScrollerApiRef={undefined}
          direction="vertical"
          listSize={carousels.length}
          initialIndex={0}
          easing="power1.out"
          viewportSize={viewportHeight}
          viewportCrossAxisSize={viewportWidth}
          enableRoamingFocus={false}
          centerFocusedTile={true}
          itemWidth={viewportWidth}
          itemHeight={carouselItemHeight}
          itemGap={0}
          overscan={2}
          startPadding={VERTICAL_PADDING}
          endPadding={VERTICAL_PADDING}
          renderTile={renderCarousel}
          getItemSize={getCarouselSize}
        />
        {fpsMeter ? (
          <FPSMeter
            x={viewportWidth - 120}
            y={20}
            fontSize={16}
            fontColor={0xffffff}
            backgroundColor={0x000000}
            backgroundAlpha={0.8}
          />
        ) : null}
      </WebGLIsland>
    </Page>
  );
}
